import React, { useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, Redirect, Route, Switch, useLocation } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import { Button, FormControlLabel, MenuItem, Stack, Switch as Switcher, Tab, Tabs } from "@mui/material";
import { ReactComponent as VerticalStick } from "assets/icons/vertical-stick.svg";
import { ExtratableRepoIcon } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { Menu } from "components/popups";
import { useDealParamsHooks, useModals, useNavigate } from "hooks";
import { isNull } from "lodash";
import {
  useAccountSelector,
  useDealActions,
  useDealTitleSelector,
  useMonitoringDataFilters,
  usePickUserPermissions,
  useViewActions
} from "store";
import { MeasureDataTableFilters, UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";
import { Routes } from "../../../constants/routes";
import { DraftsTable } from "../Drafts-table/DraftsTable";
import { ManagementFees } from "../Management-fees/ManagementFees";
import { MonitoringTable } from "../Table/Table";

type TabValue = "data" | "draft" | "fees";

export const DealMonitoring: React.FC = () => {
  const [showTopInvestors, setShowTopInvestors] = useState(false);
  const [feesFormState, setFeesFormState] = useState<{ id?: number } | null>(null);
  const [emptyState, setEmptyState] = useState<Record<TabValue, boolean>>({ data: true, draft: true, fees: true });

  const { downloadDataAction, setMeasureDataTableFiltersAction: setDataFilters } = useDealActions();
  const { setCreateManyMeasureData } = useViewActions();

  const dataFilters = useMonitoringDataFilters();
  const { show_meitav_management_fees, show_global_investor_filter } = useAccountSelector() || {};
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const noAccessPermission = dealMetrics && dealMetrics === UserPermissionType.NO_ACCESS;
  const isViewerPermission = dealMetrics && dealMetrics === UserPermissionType.VIEWER;

  const { dealId } = useDealParamsHooks();
  const dealTitle = useDealTitleSelector();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { filesUploadModal } = useModals();

  const currentTab = useMemo<TabValue | undefined>(() => {
    if (matchPath(pathname, Routes.dealMonitoringData)) return "data";
    if (matchPath(pathname, Routes.dealMonitoringDrafts)) return "draft";
    if (matchPath(pathname, Routes.dealMonitoringFees)) return "fees";
  }, [pathname]);

  const isEmpty = currentTab ? emptyState[currentTab] : true;

  const setIsEmpty = useCallback(
    (value: boolean) => {
      if (currentTab) {
        setEmptyState((prev) => (prev[currentTab] === value ? prev : { ...prev, [currentTab]: value }));
      }
    },
    [currentTab]
  );

  const goToData = useCallback(() => {
    if (!dealId) return;
    navigate({ route: "dealMonitoringData", setQueriesMethod: "replace", params: { deal_id: dealId } });
  }, [dealId]);

  const goToDrafts = useCallback(() => {
    if (!dealId) return;
    navigate({ route: "dealMonitoringDrafts", setQueriesMethod: "replace", params: { deal_id: dealId } });
  }, [dealId]);

  const goToFees = useCallback(() => {
    if (!dealId) return;
    navigate({ route: "dealMonitoringFees", setQueriesMethod: "replace", params: { deal_id: dealId } });
  }, [dealId]);

  const updateDataFilters = useCallback(
    (params: Partial<MeasureDataTableFilters> | null) => {
      if (isNull(params)) {
        return setDataFilters(null);
      }
      setDataFilters({ ...dataFilters, ...params });
    },
    [dataFilters]
  );

  const onAddDataClick = useCallback(() => {
    if (!dealId) return;
    return setCreateManyMeasureData({ dealId, dealTitle: dealTitle });
  }, [dealId, dealTitle, setCreateManyMeasureData]);

  const onDownloadData = useCallback(
    ({ hideInvestors }: { hideInvestors: boolean }) => {
      if (dealId) {
        return downloadDataAction({ deal_ids: [dealId], filters: dataFilters, hideInvestors });
      }

      const selectedDeals: number[] = dataFilters.deals.map((data) => {
        return data.id;
      });
      downloadDataAction({ deal_ids: selectedDeals, filters: dataFilters, hideInvestors });
    },
    [dealId, dataFilters]
  );

  useEffect(() => {
    if (!show_global_investor_filter) {
      setDataFilters({ investors: [] });
    }
    return () => setDataFilters(null);
  }, []);

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
    >
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          overflowY: "hidden",
          bgcolor: "white",
          borderRadius: "8px",
          p: "10px 16px 16px"
        }}
      >
        <Stack
          direction="row"
          mb="30px"
          borderBottom="1px solid #dae0e2"
          justifyContent="space-between"
          alignItems="center"
        >
          <Tabs value={currentTab} sx={{ ".vertical-stick": { m: "auto 4px" } }}>
            <Tab value="data" label="Data" onClick={goToData} sx={{ textTransform: "none", padding: "12px 0" }} />
            <VerticalStick width="3px" fill="#CED6D8" className="vertical-stick" />
            <Tab value="draft" label="Drafts" onClick={goToDrafts} sx={{ textTransform: "none", padding: "12px 0" }} />
            {show_meitav_management_fees && <VerticalStick width="3px" fill="#CED6D8" className="vertical-stick" />}
            {show_meitav_management_fees && (
              <Tab
                value="fees"
                label="Management Fees"
                onClick={goToFees}
                sx={{ textTransform: "none", padding: "12px 12px" }}
              />
            )}
          </Tabs>

          {matchPath(pathname, Routes.dealMonitoringFees) ? (
            <RoleComponentGuard
              availableRoles={[RoleName.Admin, RoleName.Analyst]}
              hideChild={noAccessPermission || isViewerPermission || isEmpty}
            >
              <Button size="sm" variant="contained" onClick={() => setFeesFormState({})} startIcon={<AddIcon />}>
                Add Fee
              </Button>
            </RoleComponentGuard>
          ) : (
            <RoleComponentGuard
              availableRoles={[RoleName.Admin, RoleName.Analyst]}
              hideChild={noAccessPermission || isViewerPermission || isEmpty}
            >
              <Stack direction="row" gap="20px">
                <Menu
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  iconButtonProps={{ sx: { width: "auto" } }}
                  icon={<Button variant="contained">Add Value</Button>}
                  options={[
                    {
                      title: "Add Value",
                      icon: <PostAddOutlinedIcon sx={{ color: "#0a3142" }} />,
                      onClick: onAddDataClick
                    },
                    {
                      title: "Data Extractor",
                      icon: <ExtratableRepoIcon sx={{ width: 20, height: 16, fill: "#071F35" }} />,
                      onClick: () => {
                        filesUploadModal({ dealId, openDataExtractor: true, acceptFileTypes: ".pdf" });
                      }
                    }
                  ]}
                />
                <Menu
                  options={[
                    {
                      title: "Export With Investors",
                      icon: <FileDownloadIcon />,
                      onClick: () => onDownloadData({ hideInvestors: false })
                    },
                    {
                      title: "Export Without Investors",
                      icon: <FileDownloadIcon />,
                      onClick: () => onDownloadData({ hideInvestors: true })
                    },
                    () => (
                      <MenuItem>
                        <FormControlLabel
                          control={
                            <Switcher
                              checked={showTopInvestors}
                              onChange={(e) => {
                                setShowTopInvestors(e.target.checked);
                                // refetch();
                              }}
                            />
                          }
                          label={showTopInvestors ? "Hide Entity Hierarchy" : "Show Entity Hierarchy"}
                        />
                      </MenuItem>
                    )
                  ]}
                />
              </Stack>
            </RoleComponentGuard>
          )}
        </Stack>
        <Switch>
          <Redirect exact from={Routes.dealMonitoring} to={Routes.dealMonitoringData} />
          <Route
            path={Routes.dealMonitoringData}
            render={() => (
              <MonitoringTable
                dealId={dealId}
                showTopInvestors={showTopInvestors}
                setFilters={updateDataFilters}
                setIsEmpty={setIsEmpty}
              />
            )}
          />
          <Route
            path={Routes.dealMonitoringDrafts}
            render={() => <DraftsTable dealId={dealId} dealTitle={dealTitle} setIsEmpty={setIsEmpty} />}
          />
          {show_meitav_management_fees && (
            <Route
              path={Routes.dealMonitoringFees}
              render={() => (
                <ManagementFees
                  feesFormState={feesFormState}
                  dealId={dealId}
                  setFeesFormState={setFeesFormState}
                  setIsEmpty={setIsEmpty}
                />
              )}
            />
          )}
        </Switch>
      </Stack>
    </RoleComponentGuard>
  );
};
